import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';

// import { router } from '@uni/router/router';
import { Router, RouterModule } from '@angular/router';

/**
 * Either a `[routerLink]`-compatible string/string-array optionally containing
 * the substring `:VALUE:` which will be replaced with the cell value; or a
 * function that takes a params object with the row's data and the cell's value
 * and returns a `[routerLink]`-compatible string/string-array.
 */
export type AgGridRouterLinkCellParamRouterLinkFormatter =
  | string
  | string[]
  | ((params :IAgGridRouterLinkCellParams) => string | string[]);

export interface IAgGridRouterLinkCellParams extends ICellRendererParams {
  routerLinkFormatter :AgGridRouterLinkCellParamRouterLinkFormatter;
  routerLinkOptions ?:{
    openInNew ?:boolean;
  };
}

const _DEFAULT_OPTIONS :IAgGridRouterLinkCellParams['routerLinkOptions'] = {
  openInNew: false,
};

export class AgGridRouterLinkCellError extends Error {}

@Component({
  selector: 'lum-ag-grid-router-link-cell',
  templateUrl: './ag-grid-router-link-cell.component.html',
  styleUrls: ['./ag-grid-router-link-cell.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule],
})
export class AgGridRouterLinkCellComponent implements ICellRendererAngularComp {
  params :IAgGridRouterLinkCellParams;
  routerLink :string | string[];
  href :string;
  value :string;
  target :string | null = null;

  options :IAgGridRouterLinkCellParams['routerLinkOptions'] = {
    ..._DEFAULT_OPTIONS,
  };

  constructor(private router: Router) {}

  agInit(params :IAgGridRouterLinkCellParams) {
    if (!params.routerLinkFormatter) {
      throw new AgGridRouterLinkCellError(
        `'routerLinkFormatter' param is required`,
      );
    }

    this.params = params;
    this.options = Object.assign({}, this.options, params.routerLinkOptions);

    this.routerLink = this._buildRouterLink(params);
    this.href = this._getHref();
    this.value = this.params.formatValue
      ? this.params.formatValue(this.params.value)
      : this.params.value;

    if (this.options.openInNew) {
      this.target = '_blank';
    }
  }

  refresh(params :IAgGridRouterLinkCellParams) :boolean {
    return true;
  }

  navigate(e) {
    if (this.target === '_blank') return; // let the default behavior happen

    e.preventDefault();

    if (Array.isArray(this.routerLink)) {
      this.router.navigate(this.routerLink);
    } else {
      this.router.navigateByUrl(this.routerLink);
    }
  }

  private _buildRouterLink(params :IAgGridRouterLinkCellParams) :string | string[] {
    if (typeof params.routerLinkFormatter === 'function') {
      return params.routerLinkFormatter(params);
    }

    const _replacer = (str :string) :string =>
      str.replace(/:VALUE:/g, params.value);

    return Array.isArray(params.routerLinkFormatter)
      ? params.routerLinkFormatter.map((str) => _replacer(str))
      : _replacer(params.routerLinkFormatter);
  }

  private _getHref() :string {
    if (typeof this.routerLink === 'string') {
      return this.routerLink;
    }

    if (Array.isArray(this.routerLink)) {
      return this.routerLink
        .map((path, i) => {
          return i > 0
            ? path.replace(/^\//, '').replace(/\/$/, '') // strip leading and trailing slashes
            : path.replace(/\/$/, ''); // strip only trailing slashes for the first
        })
        .join('/');
    }

    return '#';
  }
}
